import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Avatar,
  Chip,
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";

const TrainingTourModal = ({
  open,
  step,
  stepIndex,
  totalSteps,
  onClose,
  onNext,
  onPrevious,
  onFinish,
}) => {
  if (!step) {
    return null;
  }

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const progressValue = ((stepIndex + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (isLast) {
      onFinish();
      return;
    }
    onNext();
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-labelledby="training-tour-title"
    >
      <DialogTitle id="training-tour-title">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AutoStoriesIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Platform Eğitimi
          </Typography>
          <Chip
            label={`Adım ${stepIndex + 1}/${totalSteps}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2.5 }}>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ mt: 1, height: 8, borderRadius: 8 }}
          />
        </Box>

        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            backgroundColor: "#f8fafc",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: "#EEF2FF", color: "#4338CA" }}>
              {step.icon || "📌"}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {step.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sayfa: {step.route}
              </Typography>
            </Box>
          </Stack>

          <Alert
            icon={<LightbulbOutlinedIcon fontSize="inherit" />}
            severity="info"
            sx={{ mb: 1.5, borderRadius: 2 }}
          >
            {step.description}
          </Alert>

          {Array.isArray(step.highlights) && step.highlights.length > 0 && (
            <>
              <Divider sx={{ mb: 1.2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.8 }}>
                Bu adımda dikkat et:
              </Typography>
              <List dense disablePadding>
                {step.highlights.map((item) => (
                  <ListItem key={item} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Eğitimi Kapat</Button>
        <Button onClick={onPrevious} disabled={isFirst}>
          Geri
        </Button>
        <Button variant="contained" onClick={handleNext}>
          {isLast ? "Bitir" : "İleri"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TrainingTourModal.propTypes = {
  open: PropTypes.bool.isRequired,
  step: PropTypes.shape({
    route: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.string,
    highlights: PropTypes.arrayOf(PropTypes.string),
  }),
  stepIndex: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
};

TrainingTourModal.defaultProps = {
  step: null,
};

export default TrainingTourModal;
